"use client";
import { useAnalytics } from "@areahub/adapters/src/analytics/analytics.client";
import { getWebUrl } from "@areahub/config/src";
import { Box, Button, Heading, Stack, Text } from "@chakra-ui/react";
import { ButtonLink } from "@ui/components/button";
import { useRecoilDisclosure } from "@ui/utils";
import { useRef } from "react";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { Modal } from "../modal";

/**
 * Recoil state that controls the modal's open and closed state
 */
const lockedSubcategoryModalState = atom<boolean>({
  key: "lockedSubcategoryModalState",
  default: false,
});

/**
 * Recoil state that dictates the `subcategory` that will be rendered into the modal text
 */
const lockedSubcategoryModalContent = atom<string>({
  key: "lockedSubcategoryModalContent",
  default: "",
});

/**
 * Hook that returns a function which takes a subcategory string and sets the `lockedSubcategoryModalContent` and sets the `lockedSubcategoryModalState` to true
 */
export const useLockedSubcategoryModal = () => {
  const [_subcategory, setSubcategory] = useRecoilState(
    lockedSubcategoryModalContent,
  );
  const [_modal, setModal] = useRecoilState(lockedSubcategoryModalState);
  return (subcategory: string) => {
    setSubcategory(subcategory);
    return setModal(true);
  };
};

export const LockedSubcategoryModal = () => {
  const subcategory = useRecoilValue(lockedSubcategoryModalContent);
  const { isOpen, onClose } = useRecoilDisclosure(lockedSubcategoryModalState);
  const initialRef = useRef<HTMLInputElement>(null);
  const analytics = useAnalytics();
  return (
    <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={onClose}>
      <Box>
        <Heading size="2xl" color="blue.500">
          Discover More With AreaHub's Pro
        </Heading>
        <Text mt="30px" fontSize="md">
          {`Unlock ${subcategory} and other hazard categories for only $24.99 per report. Search address-specific reports, explore detailed insights, and more with AreaHub Pro.`}
        </Text>
      </Box>
      <Stack
        w="full"
        flexDirection={{
          base: "column",
          md: "row",
        }}
        spacing={{
          base: 3,
          md: 0,
        }}
        justifyContent={{
          base: "center",
          md: "space-evenly",
        }}
        alignItems={{
          base: "center",
        }}
      >
        <Button
          borderRadius="full"
          onClick={() => {
            analytics.track("user clicked continue with free version", {
              ui: "LockedSubcategoryModal",
              subcategory,
            });
            onClose();
          }}
          colorScheme="green"
          w="250px"
          h="45px"
        >
          Continue with Free Report
        </Button>
        <ButtonLink
          href={getWebUrl() + "/individuals/pricing"}
          colorScheme="blue"
          w="250px"
          h="45px"
          borderRadius="full"
          onClick={() =>
            analytics.track("user clicked see pricing plans", {
              ui: "LockedSubcategoryModal",
              subcategory,
            })
          }
        >
          Check Out AreaHub Pro
        </ButtonLink>
      </Stack>
    </Modal>
  );
};
